import React, { useState, useRef, useEffect } from 'react';
import PhotoViewer from '../common/PhotoViewer';
import { BACKEND_ROOT } from '../../config';

// Add helpers for photo processing
const guessMimeFromName = (name = '') => {
  const n = String(name).toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.gif')) return 'image/gif';
  return 'image/jpeg';
};

const base64ToBlob = (b64, mime) => {
  const byteChars = atob(b64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mime || 'application/octet-stream' });
};

const BuildingPhotosCard = ({ building }) => {
  // Photo Viewer States  
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  // Expand/Collapse state - initially hidden as requested
  const [isExpanded, setIsExpanded] = useState(false);

  // Cache for object URLs created from raw base64
  const objectUrlCacheRef = useRef(new Map());

  // Building photo categories as specified by user
  const buildingPhotoCategories = [
    'Außenansicht', 
    'Keller', 
    'Treppenhaus', 
    'GV offen', 
    'Hausdurchbruch mit GV', 
    'Sonstige'
  ];

  // Return a displayable src for building photos:
  // - if file_path exists -> construct media URL from backend
  // - if data URL -> return as is
  // - if raw base64 -> convert to Blob URL and cache
  // - else fall back to .url
  const getBuildingPhotoSrc = (photo) => {
    if (!photo) return '';
    
    // If we have a file_path from the database, construct the media URL
    if (photo.file_path) {
      // file_path is like "/app/media/photos/filename.jpg"
      // Extract the path after /app/media/
      const mediaPath = photo.file_path.replace('/app/media/', '');
      return `${BACKEND_ROOT}/media/${mediaPath}`;
    }
    
    const d = photo.data;
    if (typeof d === 'string' && d.length) {
      if (d.startsWith('data:')) return d; // already data URL
      // plain base64 -> Blob URL
      const key =
        photo.id ||
        photo.structured_filename ||
        photo.editable_filename ||
        photo.fileName ||
        Math.random().toString(36);
      const cached = objectUrlCacheRef.current.get(key);
      if (cached) return cached;

      const mime =
        photo.mime_type ||
        guessMimeFromName(photo.structured_filename || photo.editable_filename || photo.fileName || '');
      try {
        const blob = base64ToBlob(d, mime);
        const url = URL.createObjectURL(blob);
        objectUrlCacheRef.current.set(key, url);
        return url;
      } catch {
        return '';
      }
    }
    return photo.url || '';
  };

  // Revoke object URLs when building/photos list changes or on unmount
  useEffect(() => {
    return () => {
      for (const url of objectUrlCacheRef.current.values()) {
        try { URL.revokeObjectURL(url); } catch {}
      }
      objectUrlCacheRef.current.clear();
    };
  }, [building?.id, (building?.photos || []).length]);

  const buildingPhotos = building?.photos || [];
  
  // Check if building data is still loading (building exists but has no id yet)
  const isBuildingLoading = !building || !building.id;

  // Group photos by category
  const photosByCategory = buildingPhotoCategories.reduce((acc, category) => {
    acc[category] = buildingPhotos.filter(photo => 
      (photo.category || photo.photo_type || 'Sonstige') === category
    );
    return acc;
  }, {});

  const openPhotoViewer = (photos, index) => {
    setViewerPhotos(photos);
    setCurrentPhotoIndex(index);
    setPhotoViewerOpen(true);
  };

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div 
        className="flex justify-between items-center mb-4 cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? "Einklappen" : "Ausklappen"}
      >
        <h6 className="text-sm font-medium text-gray-700">Gebäude-Fotos</h6>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {isExpanded ? "Ausblenden" : "Anzeigen"}
          </span>
          <div className="transform transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <svg 
              className="w-4 h-4 text-gray-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <>
          {isBuildingLoading ? (
            <div className="text-center py-8 text-gray-500">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                <span className="text-sm">Lade Gebäude-Fotos...</span>
              </div>
            </div>
          ) : buildingPhotos.length > 0 ? (
            <div className="space-y-4">
              {buildingPhotoCategories.map(category => {
                const categoryPhotos = photosByCategory[category];
                
                if (categoryPhotos.length === 0) return null;
                
                return (
                  <div key={category} className="border rounded-lg p-3 bg-gray-50">
                    <h6 className="text-xs font-medium text-gray-600 mb-2">{category}</h6>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {categoryPhotos.map((photo, index) => {
                        const displaySrc = getBuildingPhotoSrc(photo);
                        const fname = photo.fileName || photo.structured_filename || photo.editable_filename || '';
                        
                        return (
                          <div key={index} className="relative">
                            {displaySrc ? (
                              <img
                                src={displaySrc}
                                alt={category}
                                className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openPhotoViewer(categoryPhotos, index)}
                              />
                            ) : (
                              <div className="w-full h-24 flex items-center justify-center text-xs text-gray-400 border rounded">
                                No Preview
                              </div>
                            )}
                            {fname && (
                              <div className="text-xs text-gray-500 mt-1 text-center truncate" title={fname}>
                                {fname}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">Keine Gebäude-Fotos vorhanden</p>
              <p className="text-xs mt-1">Gebäude-Fotos werden beim Erstellen des Gebäudes hochgeladen</p>
            </div>
          )}
        </>
      )}

      {/* Photo Viewer Modal */}
      {photoViewerOpen && (
        <PhotoViewer
          photos={viewerPhotos}
          currentIndex={currentPhotoIndex}
          onClose={() => setPhotoViewerOpen(false)}
          onNext={(index) => setCurrentPhotoIndex(index)}
          onPrev={(index) => setCurrentPhotoIndex(index)}
          getPhotoSrc={getBuildingPhotoSrc}
        />
      )}
    </div>
  );
};

export default BuildingPhotosCard;
